import { Controller } from "@hotwired/stimulus"
import fountain from "../vendor/fountain"

export default class extends Controller {
  static targets = [
    "titlePage", "mainScript", "fountainModal",
    "editorArea", "previewArea", "textarea", "editBtn",
    "form", "saveError",
    "scrollContainer", "pageEstimator", "currentPage", "totalPages", "pageLabel", "pageDivider"
  ]

  static values = {
    projectTitle: String,
    editing: { type: Boolean, default: false }
  }

  connect() {
    // Initialize Lucide icons
    if (window.lucide) {
      window.lucide.createIcons()
    }

    // Setup autosave
    this.autosaveTimeout = null
    this.totalPageCount = 1

    // Render the preview on connect
    this.renderPreview()
    this.calculateTotalPages()
  }

  disconnect() {
    if (this.autosaveTimeout) {
      clearTimeout(this.autosaveTimeout)
    }
  }

  renderPreview() {
    // Get script body from hidden element or textarea
    let text
    if (this.hasTextareaTarget) {
      text = this.textareaTarget.value
    } else {
      const scriptBodyElement = document.getElementById("script-body-data")
      if (scriptBodyElement) {
        text = scriptBodyElement.textContent
      }
    }

    if (!text) return

    try {
      const output = fountain.parse(text)
      if (this.hasTitlePageTarget) {
        this.titlePageTarget.innerHTML = output.html.title_page || ""
      }
      if (this.hasMainScriptTarget) {
        this.mainScriptTarget.innerHTML = output.html.script || ""
      }
    } catch (e) {
      console.error("Fountain parsing error:", e)
      if (this.hasMainScriptTarget) {
        this.mainScriptTarget.innerHTML = "<p class='text-red-500'>Error parsing screenplay format</p>"
      }
    }
  }

  // Toggle between edit and preview modes
  toggleEdit() {
    if (this.editingValue) {
      this.switchToPreview()
    } else {
      this.switchToEdit()
    }
  }

  switchToEdit() {
    this.editingValue = true
    if (this.hasPreviewAreaTarget) {
      this.previewAreaTarget.classList.add("hidden")
    }
    if (this.hasEditorAreaTarget) {
      this.editorAreaTarget.classList.remove("hidden")
    }
    if (this.hasEditBtnTarget) {
      this.editBtnTarget.textContent = "Preview screenplay format"
    }
    if (this.hasTextareaTarget) {
      this.textareaTarget.focus()
    }
  }

  switchToPreview() {
    this.editingValue = false
    // Re-render preview with latest content
    this.renderPreview()
    if (this.hasEditorAreaTarget) {
      this.editorAreaTarget.classList.add("hidden")
    }
    if (this.hasPreviewAreaTarget) {
      this.previewAreaTarget.classList.remove("hidden")
    }
    if (this.hasEditBtnTarget) {
      this.editBtnTarget.textContent = "Edit screenplay"
    }
  }

  // Autosave functionality
  scheduleAutosave() {
    if (this.autosaveTimeout) {
      clearTimeout(this.autosaveTimeout)
    }
    this.autosaveTimeout = setTimeout(() => this.autosave(), 2000)
    // Recalculate pages as user types
    this.calculateTotalPages()
  }

  async autosave() {
    if (!this.hasTextareaTarget || !this.hasFormTarget) return

    try {
      const response = await fetch(this.formTarget.action, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
          "Accept": "application/json"
        },
        body: JSON.stringify({
          script: { body: this.textareaTarget.value }
        })
      })

      if (response.ok) {
        this.hideSaveError()
      } else {
        console.error("Autosave failed")
        this.showSaveError()
      }
    } catch (error) {
      console.error("Autosave error:", error)
      this.showSaveError()
    }
  }

  showSaveError() {
    if (this.hasSaveErrorTarget) {
      this.saveErrorTarget.classList.remove("hidden")
      if (window.lucide) window.lucide.createIcons()
    }
  }

  hideSaveError() {
    if (this.hasSaveErrorTarget) {
      this.saveErrorTarget.classList.add("hidden")
    }
  }

  downloadPdf() {
    // Get text from textarea if editing, otherwise from hidden element
    let text
    if (this.hasTextareaTarget) {
      text = this.textareaTarget.value
    } else {
      const scriptBodyElement = document.getElementById("script-body-data")
      if (scriptBodyElement) {
        text = scriptBodyElement.textContent
      }
    }

    if (!text) {
      alert("Please write some content before downloading")
      return
    }

    if (typeof AWPDF === "undefined") {
      alert("PDF library not loaded. Please refresh the page and try again.")
      return
    }

    try {
      AWPDF.setScript(text)
      AWPDF.getPdf((result) => {
        const filename = `${this.projectTitleValue.replace(/\s+/g, "_")}_screenplay.pdf`
        this.saveBlob(result.blob, filename)
      })
    } catch (e) {
      console.error("PDF generation error:", e)
      alert("Error generating PDF. Please try again.")
    }
  }

  saveBlob(blob, filename) {
    if (typeof saveAs !== "undefined") {
      saveAs(blob, filename)
    } else {
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }
  }

  showFountainInfo() {
    this.fountainModalTarget.classList.remove("hidden")
    if (window.lucide) {
      window.lucide.createIcons()
    }
  }

  closeFountainInfo() {
    this.fountainModalTarget.classList.add("hidden")
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  // Page estimation - roughly 200 words per screenplay page
  calculateTotalPages() {
    let text = this.getScriptText()
    if (!text) {
      this.totalPageCount = 1
      this.updatePageDisplay(1, 1)
      return
    }

    // Remove title page metadata (lines starting with key:value at the top)
    const lines = text.split('\n')
    let scriptStartIndex = 0
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim()
      // Title page entries are key:value format or blank lines at the start
      if (line === '' || /^[a-zA-Z\s]+:/.test(line)) {
        scriptStartIndex = i + 1
      } else {
        break
      }
    }

    const scriptContent = lines.slice(scriptStartIndex).join('\n')
    const words = scriptContent.trim().split(/\s+/).filter(w => w.length > 0)

    // ~200 words per page for screenplays (conservative estimate)
    this.totalPageCount = Math.max(1, Math.ceil(words.length / 200))
    this.updatePageDisplay(1, this.totalPageCount)
  }

  getScriptText() {
    if (this.hasTextareaTarget) {
      return this.textareaTarget.value
    }
    const scriptBodyElement = document.getElementById("script-body-data")
    if (scriptBodyElement) {
      return scriptBodyElement.textContent
    }
    return null
  }

  updatePageEstimate() {
    if (!this.hasScrollContainerTarget) return

    const container = this.scrollContainerTarget
    const scrollTop = container.scrollTop
    const scrollHeight = container.scrollHeight - container.clientHeight

    // At the very top = title page
    if (scrollTop < 50) {
      this.updatePageDisplay("Title", this.totalPageCount)
      return
    }

    if (scrollHeight <= 0 || this.totalPageCount <= 1) {
      this.updatePageDisplay(1, this.totalPageCount)
      return
    }

    // Calculate current page (1 to totalPageCount), accounting for title page in scroll
    const scrollPercent = scrollTop / scrollHeight
    const totalWithTitle = this.totalPageCount + 1
    const rawPage = Math.ceil(scrollPercent * totalWithTitle)
    const currentPage = Math.min(this.totalPageCount, Math.max(1, rawPage - 1))

    this.updatePageDisplay(currentPage, this.totalPageCount)
  }

  updatePageDisplay(current, total) {
    const isTitlePage = current === "Title"

    if (this.hasPageLabelTarget) {
      this.pageLabelTarget.textContent = isTitlePage ? "" : "Page"
    }
    if (this.hasCurrentPageTarget) {
      this.currentPageTarget.textContent = isTitlePage ? "Title Page" : current
    }
    if (this.hasPageDividerTarget) {
      this.pageDividerTarget.classList.toggle("hidden", isTitlePage)
    }
    if (this.hasTotalPagesTarget) {
      this.totalPagesTarget.textContent = total
      this.totalPagesTarget.classList.toggle("hidden", isTitlePage)
    }
  }
}
